/**
 * Constraint resolution — v2 port of `gantt/sources/ext/auto_scheduling/constraints.ts`.
 *
 * Implements:
 *  - `resolveConstraintType(snapshot, taskId)` per D-24 (groupMode suppresses
 *    parent inheritance) + D-26 (summary constraint matrix).
 *  - `getConstraintLimits(snapshot, taskId)` — pure derivation from
 *    `(constraint_type, constraint_date, duration, calendar)` with NO
 *    `null > date` comparisons (Part I §6 root cause).
 *
 * v1's `processConstraint` mixed plan-mutation with bound derivation; v2
 * separates them: `getConstraintLimits` returns only the bounds; the
 * scheduling passes (asap/alap) apply them as inputs to `Math.max` /
 * `Math.min` against predecessor/successor-derived candidates.
 */

import type { ConstraintType, Snapshot, TaskID } from "../types";
import { normalizeTaskId } from "../ids";

export interface ConstraintLimits {
  /** Earliest allowed start (epoch ms). `undefined` ⇒ no lower bound. */
  readonly earliestStart?: number;
  /** Latest allowed start (epoch ms). `undefined` ⇒ no upper bound. */
  readonly latestStart?: number;
  /** Earliest allowed end (epoch ms). `undefined` ⇒ no lower bound. */
  readonly earliestEnd?: number;
  /** Latest allowed end (epoch ms). `undefined` ⇒ no upper bound. */
  readonly latestEnd?: number;
}

/** Six hard constraint types — surface as bounds via `getConstraintLimits`. */
const HARD: ReadonlySet<ConstraintType> = new Set<ConstraintType>([
  "snet", "snlt", "fnet", "fnlt", "mso", "mfo"
]);

function defaultConstraintType(scheduleFromEnd: boolean): ConstraintType {
  return scheduleFromEnd ? "alap" : "asap";
}

/**
 * Resolve the effective constraint type for a task.
 *
 * Per D-24: when `groupMode: true`, parent-constraint inheritance under
 * `projectConstraint: true` is suppressed. This matches v1's GS-2231
 * behavior (`gantt.getState().group_mode` check at constraints.ts:62).
 *
 * Per D-26: summaries accept the six hard constraints when explicitly set;
 * they reject ASAP/ALAP as data (those are scheduling defaults). When a
 * summary's own constraint is ASAP/ALAP (or unset), it resolves to the
 * direction default — never to an inherited type.
 *
 * Per `04-` §16.4: a task with no own constraint and no inheritance falls
 * back to the direction default (ASAP forward, ALAP backward).
 */
export function resolveConstraintType(snapshot: Snapshot, taskId: TaskID): ConstraintType {
  const normalizedTaskId = normalizeTaskId(taskId);
  const task = snapshot.tasks.get(normalizedTaskId);
  const config = snapshot.config;
  const fallback = defaultConstraintType(config.scheduleFromEnd);
  if (!task) return fallback;

  // D-26: summaries get scheduled by translation/aggregation, not by ASAP/ALAP
  // bound resolution. If a summary has an explicit hard constraint, honor it;
  // otherwise it falls to the direction default. ASAP/ALAP set as `constraint_type`
  // on a summary is treated as "no explicit constraint" (matches public docs).
  if (task.kind === "summary") {
    const own = task.constraintType;
    if (own && HARD.has(own)) return own;
    return fallback;
  }

  const own = task.constraintType;
  if (own && own !== "asap" && own !== "alap") return own;

  // D-24: parent-constraint inheritance only when projectConstraint is on
  // AND groupMode is off.
  if (config.projectConstraint && !config.groupMode) {
    for (const ancestorId of snapshot.hierarchy.ancestorsOf(normalizedTaskId)) {
      const ancestor = snapshot.tasks.get(ancestorId);
      if (!ancestor) continue;
      const pc = ancestor.constraintType;
      if (pc && pc !== "asap" && pc !== "alap") return pc;
    }
  }

  // Own ASAP/ALAP (explicit) wins over the direction default.
  if (own === "asap" || own === "alap") return own;

  return fallback;
}

/**
 * Compute the hard-constraint bounds for a task.
 *
 * Returns `undefined` for any bound that the task's constraint does NOT
 * impose. The scheduling-pass code uses `Math.max(start, earliestStart ?? -Infinity)`
 * etc., so undefined bounds are no-ops — never coerced to 0 (Part I §6 fix).
 *
 * For ASAP/ALAP or any task without a `constraintDate`, all four bounds are
 * `undefined`.
 */
export function getConstraintLimits(snapshot: Snapshot, taskId: TaskID): ConstraintLimits {
  const normalizedTaskId = normalizeTaskId(taskId);
  const task = snapshot.tasks.get(normalizedTaskId);
  if (!task) return {};

  const constraintType = resolveConstraintType(snapshot, normalizedTaskId);

  // ASAP/ALAP carry no date-based bounds; they're scheduling preferences.
  if (constraintType === "asap" || constraintType === "alap") return {};

  // Hard constraint requires a constraint_date; without one, no bounds.
  const cd = task.constraintDate;
  if (cd === undefined || !isValidDate(cd)) return {};

  const cdTs = cd.getTime();
  const calendar = snapshot.calendars.resolveForTask(normalizedTaskId);

  // Pre-compute end-from-start and start-from-end in the task's own
  // calendar; for milestones (duration === 0) both shortcut to cdTs.
  const endFromStart = task.duration === 0
    ? cdTs
    : calendar.addDuration(new Date(cdTs), task.duration, task.durationUnit).getTime();
  const startFromEnd = task.duration === 0
    ? cdTs
    : calendar.addDuration(new Date(cdTs), -task.duration, task.durationUnit).getTime();

  switch (constraintType) {
    case "snet":
      return { earliestStart: cdTs, earliestEnd: endFromStart };
    case "snlt":
      return { latestStart: cdTs, latestEnd: endFromStart };
    case "fnet":
      return { earliestStart: startFromEnd, earliestEnd: cdTs };
    case "fnlt":
      return { latestStart: startFromEnd, latestEnd: cdTs };
    case "mso":
      return {
        earliestStart: cdTs,
        latestStart: cdTs,
        earliestEnd: endFromStart,
        latestEnd: endFromStart
      };
    case "mfo":
      return {
        earliestStart: startFromEnd,
        latestStart: startFromEnd,
        earliestEnd: cdTs,
        latestEnd: cdTs
      };
    default:
      return {};
  }
}

function isValidDate(d: Date): boolean {
  return d instanceof Date && !isNaN(d.getTime());
}
